import { Prepaid } from "../../http/protocol";
import { RewardsConfigService } from "../../services/RewardsConfigService";

/** Hub88 expresses a prepaid's bet_value as amount * 100000 (5 fractional digits). */
export const HUB88_BET_VALUE_SCALAR = 100000;

/** The game+currency a prepaid list depends on; identity used to skip redundant fetches and drop stale responses. */
export interface Hub88PrepaidTarget {
  gameCode: string;
  currency: string;
}

/** A stable comparison key for a target, so a re-render only refetches when the game or currency actually changes. */
export function hub88PrepaidKey(target?: Hub88PrepaidTarget): string | undefined {
  return target ? target.gameCode + "|" + target.currency : undefined;
}

/** The amount a prepaid grants per spin (its fixed-point bet_value in whole currency units). */
export function prepaidAmount(prepaid: Prepaid): number | undefined {
  return prepaid.bet_value != null ? prepaid.bet_value / HUB88_BET_VALUE_SCALAR : undefined;
}

/** The prepaid backing a chosen amount, matched on the integer bet_value to avoid float equality. */
export function findPrepaidByAmount(prepaids: Prepaid[] | undefined, amount?: number): Prepaid | undefined {
  if (amount == null) {
    return undefined;
  }
  const betValue = Math.round(amount * HUB88_BET_VALUE_SCALAR);
  return (prepaids ?? []).find((prepaid) => prepaid.bet_value === betValue);
}

/** Prepaids sorted by ascending stake, the order the pickers present them in. */
export function prepaidsByStake(prepaids: Prepaid[] | undefined): Prepaid[] {
  return (prepaids ?? []).slice().sort((left, right) => (left.bet_value ?? 0) - (right.bet_value ?? 0));
}

/** A prepaid fetch normalised so callers only branch on `unavailable`. */
export interface Hub88PrepaidsResult {
  prepaids: Prepaid[];
  unavailable: boolean;
}

/** Fetch the prepaids for a game+currency, mapping any failure to an empty list flagged unavailable. */
export function loadHub88Prepaids(service: RewardsConfigService, target: Hub88PrepaidTarget): Promise<Hub88PrepaidsResult> {
  return service
    .getHub88Prepaids(target.gameCode, target.currency)
    .then((prepaids) => ({ prepaids: prepaids ?? [], unavailable: false }))
    .catch((error) => {
      console.error("Failed to fetch Hub88 prepaids", error);
      return { prepaids: [], unavailable: true };
    });
}
